import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

// components
import { Typography } from '../../components/Wrappers/Wrappers';
import Widget from '../../components/Widget/Widget';
import {
    Breadcrumb,
    FormFooter,
    TreatmentPlan,
    CaseAttributes,
    CaseDesign,
    CaseMethod,
    FileAttachment,
    OrderConfirmation,
    InvoicingDelivery,
} from './components';

// context
import { useOrderDispatch, useOrderState, actions as orderActions } from '../../context/OrderContext';
import { usePracticeDispatch, usePracticeState, actions as practiceActions } from '../../context/PracticeContext';

// form helper
import {
    emptyOrder,
    invoicingRecipientAttributes,
    caseDesignAttributes,
    deliveryAddressAttributes,
    findMissingRequiredFields,
    defaultFormStepsTitle,
    hasMissingRequiredFields,
    scanMethod,
    formatOrderBeforeSubmission,
} from './helpers/form';

import { otherOption } from './components/InvoicingDelivery';

const applyTreatmentTypeFromInitialOrder = (order) => {
    if (order.treatmentType.match('3 to 3')) {
        return '';
    }
    if (order.treatmentType.match('7 to 7')) {
        return '7 to 7 (2 refinements)';
    } else {
        return '5 to 5 (1 refinement)';
    }
};

const AddOrder = () => {
    const orderDispatch = useOrderDispatch();
    const orderState = useOrderState();

    const practiceDispatch = usePracticeDispatch();
    const practiceState = usePracticeState();

    const history = useHistory();
    const location = useLocation();

    const [newOrder, setNewOrder] = useState(emptyOrder());
    const [activeStep, setActiveStep] = useState(0);
    const [acceptTC, setAcceptTC] = useState(false);
    const [error, setError] = useState({});

    const provideTreatmentPlan = newOrder.provideTreatmentPlan === 'Yes';
    const allowFullArchTreatment = practiceState.currentPractice?.allowFullArchTreatment === 'Yes' ?? false;

    // ===== Hooks  =====

    // reset form and load the practices for selection
    useEffect(() => {
        orderActions.doNewForm()(orderDispatch);
        // if (!userState.isOrtho) setNewOrder({ ...newOrder, provideTreatmentPlan: 'No' })
        if (!practiceState.practices) practiceActions.doFetch()(practiceDispatch);
    }, []); // eslint-disable-line

    // find the order when ids are passed as query strings
    useEffect(() => {
        const queryString = new URLSearchParams(location.search);
        const practiceId = queryString.get('practiceId');
        const orderId = queryString.get('orderId');
        if (practiceId && orderId) {
            orderActions.doFind(practiceId, orderId)(orderDispatch);
        } else {
            orderActions.doNewForm()(orderDispatch);
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    // pre-load with current order when editing
    useEffect(() => {
        const currentOrder = orderState.currentOrder;
        if (currentOrder) {
            currentOrder.treatmentType = applyTreatmentTypeFromInitialOrder(currentOrder);
            setNewOrder(currentOrder);
        } else {
            setNewOrder(emptyOrder());
        }
    }, [orderState.currentOrder]);

    useEffect(() => {
        if (newOrder.practiceId) {
            practiceActions.doFind(newOrder.practiceId)(practiceDispatch);
        }
    }, [newOrder.practiceId, practiceDispatch]);

    // ===== Navigation  =====

    const goNext = (step = 1) => setActiveStep((prevActiveStep) => prevActiveStep + step);

    const goBack = (step = 1) => setActiveStep((prevActiveStep) => prevActiveStep - step);

    const checkTreatmentPlan = () => {
        if (newOrder.provideTreatmentPlan === '') {
            toast.error('Please select an option');
        } else {
            goNext();
        }
    };

    const checkCaseAttributes = () => {
        const { practiceId, prescriberName, patientName } = newOrder;
        const isValidPrescriberName = prescriberName.trim().length;
        const isValidPatientName = patientName.trim().length;

        if (practiceId && isValidPrescriberName && isValidPatientName) return goNext();
        setError({
            ...error,
            practiceId: !practiceId,
            prescriberName: !isValidPrescriberName,
            patientName: !isValidPatientName,
        });
    };

    const checkCaseDesign = () => {
        const caseDesignForm = caseDesignAttributes(newOrder, allowFullArchTreatment);
        const requiredFieldErrors = findMissingRequiredFields(caseDesignForm, newOrder);
        setError({ ...error, ...requiredFieldErrors });
        if (!hasMissingRequiredFields(requiredFieldErrors)) provideTreatmentPlan ? goNext(2) : goNext();
    };

    const checkMethod = () => {
        if (!newOrder.method) {
            toast.error('Please select an option');
        } else {
            goNext();
        }
    };

    const checkFileAttachment = () => {
        if (!orderState.photosUpload) {
            toast.error("Please upload the patient's files");
        } else {
            goNext();
        }
    };

    const checkInvoicingAndDelivery = () => {
        let isValidInvoicing = true;
        if (newOrder.invoicingOption === otherOption) {
            const requiredFieldErrors = findMissingRequiredFields(invoicingRecipientAttributes, newOrder);
            isValidInvoicing = !hasMissingRequiredFields(requiredFieldErrors);
            setError({ ...error, ...requiredFieldErrors });
        }
        if (!isValidInvoicing) return;

        let isValidDelivery = true;
        if (newOrder.deliveryOption === otherOption) {
            const requiredFieldErrors = findMissingRequiredFields(deliveryAddressAttributes, newOrder);
            isValidDelivery = !hasMissingRequiredFields(requiredFieldErrors);
            setError({ ...error, ...requiredFieldErrors });
        }
        if (!isValidDelivery) return;

        if (!acceptTC) {
            toast.error('You must accept the Terms & Conditions');
        } else {
            doSubmit(newOrder, history);
            goNext();
        }
    };

    const checkOrderConfirmation = () => history.push('/app/dashboard');

    const formStepsValidation = [
        checkTreatmentPlan,
        checkCaseAttributes,
        checkCaseDesign,
        checkMethod,
        checkFileAttachment,
        checkInvoicingAndDelivery,
        checkOrderConfirmation,
    ];

    // ===== Handlers  =====

    const handleTextFieldChange = (e) => {
        if (e.target.required) setError({ ...error, [e.target.name]: !e.target.value });
        // prevent the search field from updating the newOrder
        if (e.target.name === 'practiceSearch') return;

        setNewOrder({ ...newOrder, [e.target.name]: e.target.value });
    };

    /**
     * Use emptyOrder to reset the potential properties set before. We do not want to save irrelevant properties
     * Also set the method to Scan when treatment is provided
     */
    const handleNeedTreatmentPlan = (provideTreatmentPlan) => (_) => {
        setNewOrder({ ...emptyOrder(), provideTreatmentPlan, method: provideTreatmentPlan ? scanMethod : '' });
    };

    const handleMethodClicked = (method) => (_) => setNewOrder({ ...newOrder, method });

    const handleNext = async () => formStepsValidation[activeStep]();

    const handleBack = () => {
        const avoidMethodStep = provideTreatmentPlan && activeStep === 4;
        avoidMethodStep ? goBack(2) : goBack();
    };

    const doSubmit = async (formValues) => {
        const data = formatOrderBeforeSubmission(formValues, practiceState.currentPractice, orderState.photosUpload);
        data.practiceId = practiceState.currentPractice.practiceId;
        data.territory = practiceState.currentPractice.territory;
        // create a new order
        await orderActions.doCreate(data)(orderDispatch);
    };

    const formStepsComponent = [
        <TreatmentPlan newOrder={newOrder} needTreatmentPlanClicked={handleNeedTreatmentPlan} />,
        <CaseAttributes newOrder={newOrder} formErrors={error} onTextFieldChange={handleTextFieldChange} />,
        <CaseDesign
            newOrder={newOrder}
            allowFullArchTreatment={allowFullArchTreatment}
            formErrors={error}
            onTextFieldChange={handleTextFieldChange}
        />,
        <CaseMethod newOrder={newOrder} methodClicked={handleMethodClicked} />,
        <FileAttachment newOrder={newOrder} />,
        <InvoicingDelivery
            practice={practiceState.currentPractice}
            newOrder={newOrder}
            formErrors={error}
            onTextFieldChange={handleTextFieldChange}
            termsAccepted={acceptTC}
            termsClicked={(clicked) => setAcceptTC(clicked)}
        />,
        <OrderConfirmation newOrder={newOrder} />,
    ];

    return (
        <Grid container spacing={3}>
            <Breadcrumb activeStep={activeStep} stepsTitle={defaultFormStepsTitle} />
            <Grid item xs={12}>
                <Widget>
                    <Grid item justifyContent="center" container>
                        <Box display="flex" flexDirection="column" width={600}>
                            <Typography variant="h5" weight="medium" style={{ marginBottom: 30 }}>
                                {defaultFormStepsTitle[activeStep]}
                            </Typography>
                            {formStepsComponent[activeStep]}
                            <FormFooter
                                activeStep={activeStep}
                                isRefinement={false}
                                onClickNext={handleNext}
                                onClickBack={handleBack}
                            />
                        </Box>
                    </Grid>
                </Widget>
            </Grid>
        </Grid>
    );
};

export default AddOrder;
